import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, useId } from 'preact/hooks';
import type { ProjectPlanSummary, PlanChunkSummary } from '@voice-assistant/shared';
import { WaveProgressPanel } from './WaveProgressPanel';
import { useWaveStore, useActiveWave, usePlansRevision } from '../state/waveStore';
import { useUiState, setPlansStatusFilter } from '../state/uiStateStore';
import { timeAgo } from '../utils/modelHelpers';
import { useReaderModeOptional } from '../hooks/useReaderMode';
import { ReaderToggle } from './ReaderMode/ReaderToggle';
import { Button, HoldButton } from '../recipes/button/Recipe';

interface QueueStats {
  queueDepth: number;
  activePlanId: string | null;
  activeChunkId: string | null;
  todayCostUsd: number;
  dailyCapUsd: number;
  paused: boolean;
}

/* ── Helpers ───────────────────────────────────────────────────────── */

type StatusFilter = 'all' | 'active' | 'executing' | 'stale';

const STATUS_COLORS: Record<string, string> = {
  pending:       'var(--text-dim)',
  'in-progress': '#56a0d4',
  done:          '#4caf50',
  failed:        '#e53935',
  'needs-review':'#ff9800',
  deferred:      'var(--text-dim)',
};

const RISK_COLORS: Record<string, string> = {
  low:    '#4caf50',
  medium: '#ff9800',
  high:   '#e53935',
};

function isChunkBlocked(chunk: PlanChunkSummary, allChunks: PlanChunkSummary[]): boolean {
  if (!chunk.dependsOn?.length) return false;
  return chunk.dependsOn.some(depId => {
    const dep = allChunks.find(c => c.id === depId);
    return !dep || dep.status !== 'done';
  });
}

function getBlockedByNames(chunk: PlanChunkSummary, allChunks: PlanChunkSummary[]): string[] {
  if (!chunk.dependsOn?.length) return [];
  return chunk.dependsOn
    .map(depId => {
      const dep = allChunks.find(c => c.id === depId);
      return dep && dep.status !== 'done' ? dep.title : null;
    })
    .filter(Boolean) as string[];
}

function progressPercent(chunks: PlanChunkSummary[]): number {
  if (!chunks.length) return 0;
  const done = chunks.filter(c => c.status === 'done').length;
  return Math.round((done / chunks.length) * 100);
}

function highestRisk(chunks: PlanChunkSummary[]): string {
  const order = ['high', 'medium', 'low'];
  for (const level of order) {
    if (chunks.some(c => c.riskLevel === level)) return level;
  }
  return 'low';
}

interface ProjectGroup {
  project: string;
  plans: ProjectPlanSummary[];
}

/* ── Execution status badge ────────────────────────────────────────── */

function ExecBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    idle:            { label: 'Idle',       cls: 'plan-exec--idle' },
    executing:       { label: 'Executing',  cls: 'plan-exec--executing' },
    'awaiting-merge':{ label: 'Awaiting merge', cls: 'plan-exec--awaiting' },
    completed:       { label: 'Completed',  cls: 'plan-exec--completed' },
  };
  const info = map[status] ?? { label: status, cls: '' };
  return <span class={`plan-exec-badge ${info.cls}`}>{info.label}</span>;
}

/* ── Chunk status badge ────────────────────────────────────────────── */

function ChunkStatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || 'var(--text-dim)';
  return <span class="chunk-status-badge" style={{ background: color }}>{status}</span>;
}

/* ── PlanPhaseList — phases + chunks within an expanded PlanCard ──── */

interface PhaseListProps {
  plan: ProjectPlanSummary;
  onExecuteChunk: (planId: string, chunkId: string) => void;
  onUpdateStatus: (chunkId: string, status: string) => void;
  executingChunks: Set<string>;
}

function PlanPhaseList({ plan, onExecuteChunk, onUpdateStatus, executingChunks }: PhaseListProps) {
  const [expandedChunk, setExpandedChunk] = useState<string | null>(null);
  const chunks = plan.chunks || [];

  // Group by phaseNumber
  const phases = useMemo(() => {
    const map = new Map<number | null, PlanChunkSummary[]>();
    for (const c of chunks) {
      const key = c.phaseNumber;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    // Sort phases numerically (null last)
    return [...map.entries()].sort((a, b) => {
      if (a[0] === null) return 1;
      if (b[0] === null) return -1;
      return a[0] - b[0];
    });
  }, [chunks]);

  if (!chunks.length) {
    return <div class="plan-phase-empty">No chunks defined</div>;
  }

  return (
    <div class="plan-phases">
      {phases.map(([phaseNum, phaseChunks]) => {
        const phaseDone = phaseChunks.filter(c => c.status === 'done').length;
        const phaseTotal = phaseChunks.length;
        return (
          <div key={phaseNum ?? 'ungrouped'} class="plan-phase">
            <div class="plan-phase-header">
              <span class="plan-phase-title">
                {phaseNum !== null ? `Phase ${phaseNum}` : 'Ungrouped'}
              </span>
              <span class="plan-phase-progress">{phaseDone}/{phaseTotal}</span>
            </div>
            {phaseChunks.sort((a, b) => a.sortOrder - b.sortOrder).map(chunk => {
              const blocked = isChunkBlocked(chunk, chunks);
              const blockedBy = getBlockedByNames(chunk, chunks);
              const isExpanded = expandedChunk === chunk.id;
              const isExecuting = executingChunks.has(chunk.id);
              const canExecute = !blocked && chunk.status === 'pending' && !isExecuting;

              return (
                <div
                  key={chunk.id}
                  class={`plan-chunk${blocked ? ' plan-chunk--blocked' : ''}${isExpanded ? ' plan-chunk--expanded' : ''}`}
                  data-reader-block-id={chunk.id}
                  onClick={() => setExpandedChunk(isExpanded ? null : chunk.id)}
                >
                  <div class="plan-chunk-row">
                    {blocked && <span class="plan-chunk-lock" title={`Depends on: ${blockedBy.join(', ')}`}>🔒</span>}
                    <span class="plan-chunk-title">{chunk.title}</span>
                    <ChunkStatusBadge status={chunk.status} />
                    {chunk.effort && <span class="plan-chunk-effort">{chunk.effort}</span>}
                    <span class="plan-chunk-risk" style={{ color: RISK_COLORS[chunk.riskLevel] || 'var(--text-dim)' }}>
                      {chunk.riskLevel}
                    </span>
                  </div>
                  {blocked && blockedBy.length > 0 && (
                    <div class="plan-chunk-blocked-hint">
                      Depends on: {blockedBy.join(', ')}
                    </div>
                  )}
                  {isExpanded && (
                    <div class="plan-chunk-detail" onClick={(e) => e.stopPropagation()}>
                      {chunk.description && <div class="plan-chunk-desc">{chunk.description}</div>}
                      {chunk.validation && (
                        <div class="plan-chunk-validation">
                          <strong>Validation:</strong> {chunk.validation}
                        </div>
                      )}
                      {chunk.executionLog && (
                        <div class="plan-chunk-log">
                          <strong>Execution log:</strong>
                          <pre>{chunk.executionLog}</pre>
                        </div>
                      )}
                      {chunk.modifiedFiles && chunk.modifiedFiles.length > 0 && (
                        <div class="plan-chunk-files">
                          <strong>Modified files:</strong>
                          <ul>{chunk.modifiedFiles.map(f => <li key={f}><code>{f}</code></li>)}</ul>
                        </div>
                      )}
                      {chunk.sessionId && (
                        <div class="plan-chunk-session-link">
                          <button
                            class="plan-chunk-session-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.dispatchEvent(new CustomEvent('valko-open-session', { detail: { sessionId: chunk.sessionId } }));
                            }}
                          >⚙ View Agent Session</button>
                        </div>
                      )}
                      <div class="plan-chunk-actions">
                        {canExecute && (
                          <button
                            class="plan-chunk-exec-btn"
                            onClick={(e) => { e.stopPropagation(); onExecuteChunk(plan.id, chunk.id); }}
                          >
                            {isExecuting ? '…' : '▶ Execute'}
                          </button>
                        )}
                        {blocked && (
                          <button class="plan-chunk-exec-btn plan-chunk-exec-btn--disabled" disabled>
                            🔒 Blocked
                          </button>
                        )}
                        {chunk.status === 'pending' && !blocked && (
                          <button
                            class="plan-chunk-status-btn"
                            onClick={(e) => { e.stopPropagation(); onUpdateStatus(chunk.id, 'done'); }}
                          >✓ Mark done</button>
                        )}
                        {chunk.status === 'in-progress' && (
                          <>
                            <button
                              class="plan-chunk-status-btn plan-chunk-status-btn--done"
                              onClick={(e) => { e.stopPropagation(); onUpdateStatus(chunk.id, 'done'); }}
                            >✓ Done</button>
                            <button
                              class="plan-chunk-status-btn plan-chunk-status-btn--fail"
                              onClick={(e) => { e.stopPropagation(); onUpdateStatus(chunk.id, 'failed'); }}
                            >✗ Failed</button>
                          </>
                        )}
                        {chunk.status === 'done' && (
                          <button
                            class="plan-chunk-status-btn plan-chunk-status-btn--reset"
                            onClick={(e) => { e.stopPropagation(); onUpdateStatus(chunk.id, 'pending'); }}
                          >↺ Reset</button>
                        )}
                        {chunk.status === 'failed' && (
                          <button
                            class="plan-chunk-status-btn plan-chunk-status-btn--reset"
                            onClick={(e) => { e.stopPropagation(); onUpdateStatus(chunk.id, 'pending'); }}
                          >↺ Retry</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* ── Mermaid dependency graph generation ───────────────────────────── */

function generateDependencyMermaid(chunks: PlanChunkSummary[]): string | null {
  if (!chunks.length) return null;
  const hasAnyDeps = chunks.some(c => c.dependsOn?.length);
  if (!hasAnyDeps) return null;

  const lines: string[] = ['flowchart TD'];
  const idMap = new Map<string, string>();

  // Create short IDs for mermaid
  chunks.forEach((c, i) => {
    const shortId = `C${i}`;
    idMap.set(c.id, shortId);
    const statusIcon = c.status === 'done' ? '✓ ' : c.status === 'failed' ? '✗ ' : '';
    const label = `${statusIcon}${c.title}`;
    lines.push(`  ${shortId}["${label.replace(/"/g, "'")}"]`);
  });

  // Style nodes by status
  chunks.forEach((c, i) => {
    const shortId = `C${i}`;
    if (c.status === 'done') lines.push(`  style ${shortId} fill:#2e7d32,color:#fff`);
    else if (c.status === 'failed') lines.push(`  style ${shortId} fill:#c62828,color:#fff`);
    else if (c.status === 'in-progress') lines.push(`  style ${shortId} fill:#1565c0,color:#fff`);
    else if (isChunkBlocked(c, chunks)) lines.push(`  style ${shortId} fill:#424242,color:#888`);
  });

  // Add edges
  for (const c of chunks) {
    if (!c.dependsOn?.length) continue;
    const target = idMap.get(c.id);
    for (const depId of c.dependsOn) {
      const source = idMap.get(depId);
      if (source && target) {
        const dep = chunks.find(ch => ch.id === depId);
        const edgeStyle = dep?.status === 'done' ? '-->' : '-.->';
        lines.push(`  ${source} ${edgeStyle} ${target}`);
      }
    }
  }

  return lines.join('\n');
}

/* ── PlanConfirmDialog ─────────────────────────────────────────────── */

interface PlanConfirmDialogProps {
  action: 'archive' | 'delete';
  planName: string;
  /** The dialog closed: Cancel, Escape, the scrim, or the act. State follows the dialog, never the other way round. */
  onClose: () => void;
  /** The act itself (archive or delete); the dialog closes right after it. */
  onConfirm: () => void;
}

/**
 * The Archive / Delete question, as a native <dialog> opened with showModal() (voice-assistant#2311). The dialog element is the box
 * and its scrim is its ::backdrop. The user agent does the dialog work, so none of it is hand-rolled here:
 *   - the top layer: it paints over everything, the Agents drawer that opened it included, whatever the z-index ladder says;
 *   - the page behind it is inert, so Tab and Shift+Tab cycle inside the dialog and focus never reaches the plan card behind it;
 *   - focus lands on Cancel (the safe act) when it opens and returns to the button that opened it when it closes: Cancel is the first control of the dialog, so the dialog's own focusing steps pick it
 *     (the recipe's Button takes no autofocus prop, and its copy is read-only), and it must stay first in DOM order;
 *   - Escape closes this dialog and nothing else: the keydown stops here, so the drawer's and the door panel's own Escape
 *     handlers (DrawerHost on its scrim, DesktopDoors on document) never see a key meant for the confirm.
 * The acts differ in class (design contract law 5), and the dialog says so. All three controls are ONE kit, the estate's button recipe (src/recipes/button, copied and owned): one height (44 px on a coarse pointer),
 * one label step, one radius.
 *   - Cancel is class A, tone neutral: it acts at once and is the safe act.
 *   - Archive is class A, tone primary: a filled button that acts at once: the plan moves out of the list and can be brought back, so its question is the plain `dialog`.
 *   - Delete is class B and destructive, so its question is an `alertdialog` (WAI-ARIA) and its control is the recipe's HoldButton (PlanCard offers it only for a plan that is a database row alone, see canDelete): outlined, a pointer HOLD of
 *     the document's --est-motion-hold with progress and a clean cancel, and for the keyboard, a screen reader and a tap under reduced motion the non-timed two-step (activate arms it, activate again within
 *     4 s confirms, Esc or blur disarms). A press that is too short fires nothing; an Esc while armed disarms and closes this dialog through the Escape handling above.
 * After a confirmed act the card is gone with the button that opened the confirm, so the focus return has nowhere to land: PlansTab moves focus to its heading (settleAfterAct).
 */
function PlanConfirmDialog({ action, planName, onClose, onConfirm }: PlanConfirmDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const msgId = useId();

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const isDelete = action === 'delete';
  const close = () => dialogRef.current?.close();
  // the act, then the dialog closes (the same for a click on Archive and for a completed hold or a confirmed second step on Delete)
  const confirm = () => { onConfirm(); close(); };
  return (
    <dialog
      ref={dialogRef}
      class="plan-confirm-box"
      role={isDelete ? 'alertdialog' : undefined}
      aria-labelledby={titleId}
      aria-describedby={msgId}
      onClose={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') e.stopPropagation(); }}
      onClick={(e) => {
        // A click on the scrim lands on the dialog itself, and so does one on the box's own padding: the point decides.
        if (e.target !== e.currentTarget) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
      }}
    >
      <h3 id={titleId} class="plan-confirm-title">
        {isDelete ? 'Delete plan?' : 'Archive plan?'}
      </h3>
      <p id={msgId} class="plan-confirm-msg">
        {isDelete
          ? `This removes "${planName}" from the plans list.`
          : `This moves "${planName}" out of the list, into Archived Plans in the Files drawer.`}
      </p>
      <div class="plan-confirm-actions">
        <Button tone="neutral" onClick={close}>Cancel</Button>
        {isDelete ? (
          <HoldButton label="Delete" tone="danger" onConfirm={confirm} />
        ) : (
          <Button tone="primary" onClick={confirm}>Archive</Button>
        )}
      </div>
    </dialog>
  );
}

/* ── PlanCard ──────────────────────────────────────────────────────── */

interface ReviewSession {
  id: string;
  name: string | null;
  model: string | null;
  numTurns: number;
  createdAt: string;
}

interface PlanCardProps {
  plan: ProjectPlanSummary;
  onExecuteChunk: (planId: string, chunkId: string) => void;
  onUpdateStatus: (chunkId: string, status: string) => void;
  onViewPlan: (plan: ProjectPlanSummary) => void;
  onReviewPlan: (plan: ProjectPlanSummary) => void;
  onArchivePlan: (planId: string) => void;
  onDeletePlan: (planId: string) => void;
  executingChunks: Set<string>;
}

function PlanCard({ plan, onExecuteChunk, onUpdateStatus, onViewPlan, onReviewPlan, onArchivePlan, onDeletePlan, executingChunks }: PlanCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [reviewSessions, setReviewSessions] = useState<ReviewSession[]>([]);
  const [confirmAction, setConfirmAction] = useState<'archive' | 'delete' | null>(null);
  // Delete is offered only for a plan that is a row alone: it has no planFilePath and is not a sandbox plan. The seeder (PlansService.seedPlansFromFiles, on every list read, throttled to 60 s) finds an existing row by
  // planFilePath and creates one for every <project>/plans/*.md that has none, and deletePlan removes only the row, so a deleted plan WITH a file path is back on the next refresh. That holds for a file-backed plan
  // (source 'file') and for a memo plan too, because a memo plan's first generation registers <project>/plans/current-plan.md as its planFilePath (PlansGitService). A sandbox plan is a synthetic listing of the user's
  // sandbox files, not a row. A plan with a file path offers Archive only, which moves the file to _archive/, where the seeder does not look.
  const canDelete = !plan.planFilePath && plan.source !== 'sandbox';

  const bodyRef = useRef<HTMLDivElement>(null);
  const reader = useReaderModeOptional();
  useEffect(() => {
    if (!reader || !expanded) return;
    const el = bodyRef.current;
    if (!el) return;
    return reader.registerSurface(`plan-${plan.id}`, el);
  }, [reader, expanded, plan.id]);

  const chunks = plan.chunks || [];

  // Fetch review sessions when card expands
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch(`/api/plans/${plan.id}/sessions`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setReviewSessions(Array.isArray(data) ? data : data.sessions ?? []);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [expanded, plan.id]);
  const progress = progressPercent(chunks);
  const risk = highestRisk(chunks);
  const doneCount = chunks.filter(c => c.status === 'done').length;

  const mermaidCode = useMemo(() => generateDependencyMermaid(chunks), [chunks]);

  return (
    <div class={`plan-card${expanded ? ' plan-card--expanded' : ''}`}>
      <div class="plan-card-header" onClick={() => setExpanded(!expanded)}>
        <div class="plan-card-top">
          <span class="plan-card-name">{plan.name}</span>
          <span class="plan-card-version">v{plan.version}</span>
          <span class={`plan-card-status plan-card-status--${plan.status}`}>{plan.status}</span>
        </div>
        <div class="plan-card-meta">
          <ExecBadge status={plan.executionStatus} />
          <span class="plan-card-risk" style={{ color: RISK_COLORS[risk] }}>{risk} risk</span>
          <span class="plan-card-chunks">{doneCount}/{chunks.length} chunks</span>
          {plan.source === 'memo' && <span class="plan-card-source">💬</span>}
          {plan.source === 'file' && <span class="plan-card-source">📄</span>}
        </div>
        {/* Progress bar */}
        <div class="plan-card-progress-bar">
          <div class="plan-card-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {expanded && (
        <div class="plan-card-body" ref={bodyRef}>
          {/* Toolbar */}
          <div class="plan-card-toolbar">
            {mermaidCode && (
              <button
                class={`plan-toolbar-btn${showGraph ? ' active' : ''}`}
                onClick={() => setShowGraph(!showGraph)}
              >📈 Graph</button>
            )}
            <button
              class="plan-toolbar-btn"
              onClick={() => onViewPlan(plan)}
            >📄 View Plan</button>
            <ReaderToggle surfaceId={`plan-${plan.id}`} className="plan-toolbar-btn" />
            {plan.planFilePath && (
              <button
                class="plan-toolbar-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent('valko-share', {
                    detail: {
                      type: 'file',
                      filePath: plan.planFilePath,
                      fileName: plan.name + '.md',
                      pdfCapable: true,
                      title: plan.name,
                    },
                  }));
                }}
              >↗ Share</button>
            )}
            <button
              class="plan-toolbar-btn plan-toolbar-btn--review"
              onClick={() => onReviewPlan(plan)}
            >🔍 Review</button>
            <button
              class="plan-toolbar-btn plan-toolbar-btn--archive"
              onClick={(e) => { e.stopPropagation(); setConfirmAction('archive'); }}
            >📦 Archive</button>
            {canDelete && (
              <button
                class="plan-toolbar-btn plan-toolbar-btn--delete"
                onClick={(e) => { e.stopPropagation(); setConfirmAction('delete'); }}
              >🗑 Delete</button>
            )}
            {plan.executionBranch && (
              <span class="plan-card-branch">🌴 {plan.executionBranch}</span>
            )}
          </div>

          {/* Confirm dialog — a native <dialog> (PlanConfirmDialog above). It is still portaled to
              document.body: the top layer already lifts a modal dialog out of the transformed
              tab-track (the Plans panel sits inside sl-panels-track, translateX), and the portal
              keeps it outside the drawer host's element-level Escape handler and the plan card's
              own click handlers. */}
          {confirmAction && (confirmAction === 'archive' || canDelete) && createPortal(
            <PlanConfirmDialog
              action={confirmAction}
              planName={plan.name}
              onClose={() => setConfirmAction(null)}
              onConfirm={() => {
                if (confirmAction === 'delete') onDeletePlan(plan.id);
                else onArchivePlan(plan.id);
              }}
            />,
            document.body
          )}

          {/* Review sessions */}
          {reviewSessions.length > 0 && (
            <div class="plan-review-sessions">
              <div class="plan-review-sessions-label">Previous reviews:</div>
              {reviewSessions.slice(0, 3).map(s => (
                <button
                  key={s.id}
                  class="plan-review-session-link"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('valko-open-session', { detail: { sessionId: s.id } }));
                  }}
                >
                  {s.name || 'Review session'} · {s.numTurns} turns · {timeAgo(s.createdAt)}
                </button>
              ))}
            </div>
          )}

          {/* Mermaid dependency graph */}
          {showGraph && mermaidCode && (
            <div class="plan-dep-graph">
              <pre class="mermaid">{mermaidCode}</pre>
            </div>
          )}

          {/* Phase list with chunks */}
          <PlanPhaseList
            plan={plan}
            onExecuteChunk={onExecuteChunk}
            onUpdateStatus={onUpdateStatus}
            executingChunks={executingChunks}
          />
        </div>
      )}
    </div>
  );
}

/* ── PlansTab (main export) ────────────────────────────────────────── */

interface WaveConfig {
  lighthouseModeEnabled: boolean;
  maxConcurrentAgents: number;
  waveExecutionEnabled: boolean;
}

interface PlansTabProps {
  isAdmin: boolean;
  currentSessionId?: string | null;
  /** Protofac (SE-PF6 #1391): plans are read from the user's sandbox over SSH. Drives the
   *  empty-state hint when `plansSourceDir` is unset. */
  sandboxFsEnabled?: boolean;
}

export function PlansTab({ isAdmin, currentSessionId, sandboxFsEnabled = false }: PlansTabProps) {
  const [plans, setPlans] = useState<ProjectPlanSummary[]>([]);
  // true once a list has arrived: the heading shows its count then, never a 0 flashed before the first answer (contract law 9)
  const [listKnown, setListKnown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Class-B restorable view state (Phase 2, #1979) — owned by uiStateStore.
  const statusFilter = useUiState((s) => s.plansStatusFilter);
  const [executingChunks, setExecutingChunks] = useState<Set<string>>(new Set());
  const fetchedOnce = useRef(false);

  // Multi-select / wave mode
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(new Set());
  // Active wave is now owned by the wave/plans FSM store (#1657 Phase 2b Slice
  // 2b-waves) — this component is a pure renderer of it. Writes go through the
  // named store actions (reconcileActiveWave / closeWave), never local setState.
  const activeWave = useActiveWave();
  const [waveStarting, setWaveStarting] = useState(false);
  const [waveConfig, setWaveConfig] = useState<WaveConfig | null>(null);
  const [linkToCurrentSession, setLinkToCurrentSession] = useState(false);

  // resolves to the list it set, or null when the fetch failed (the caller that needs to know what the server now lists reads it)
  const fetchPlans = useCallback(async (): Promise<ProjectPlanSummary[] | null> => {
    setLoading(true);
    setError(null);
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch('/api/plans/list');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list: ProjectPlanSummary[] = Array.isArray(data) ? data : data.plans ?? [];
      setPlans(list);
      setListKnown(true);
      return list;
    } catch (e: any) {
      setError(e.message || 'Failed to load plans');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // The plans heading takes focus after a confirmed Delete or Archive. It is focusable by script only (tabIndex -1): never a tab stop. The effect runs after the render that dropped the card.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [headingFocus, setHeadingFocus] = useState(0);
  useEffect(() => {
    if (headingFocus > 0) headingRef.current?.focus();
  }, [headingFocus]);
  /**
   * After a confirmed Delete or Archive the plan's card is gone, and with it the button that opened the confirm: the dialog's own focus return has nowhere to land and document.activeElement falls to
   * the body. Once the refetched list no longer holds the plan, focus goes to the list's heading, whose count now carries the result (a screen reader hears it). A plan that is still listed (the act did
   * not take) leaves focus where the dialog put it, on the button that opened it. Cancel, Escape and a click on the scrim never reach here: the dialog returns focus itself.
   */
  const settleAfterAct = useCallback(async (planId: string) => {
    const list = await fetchPlans();
    if (list && !list.some((p) => p.id === planId)) setHeadingFocus((n) => n + 1);
  }, [fetchPlans]);

  // Queue stats (admin only)
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);

  const fetchQueueStats = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch('/api/plans/queue/stats');
      if (res.ok) setQueueStats(await res.json());
    } catch { /* ignore */ }
  }, [isAdmin]);

  // Fetch on first mount
  if (!fetchedOnce.current) {
    fetchedOnce.current = true;
    fetchPlans();
    fetchQueueStats();
  }

  // Re-fetch (plans + queue stats) when a plan-pipeline WS event bumps the wave/plans
  // store revision — replaces the old `va:plans-changed` window listener (R-A2, #1657
  // Phase 2b). The ref guard fires only on an actual increment AFTER this mount, so it
  // never double-fetches on top of the first-mount fetch (fetchedOnce) or on remount.
  const plansRevision = usePlansRevision();
  const lastPlansRevisionRef = useRef(plansRevision);
  useEffect(() => {
    if (plansRevision === lastPlansRevisionRef.current) return;
    lastPlansRevisionRef.current = plansRevision;
    fetchPlans();
    fetchQueueStats();
  }, [plansRevision, fetchPlans, fetchQueueStats]);

  // Wave events (wave_started/advanced/complete/failed) now flow useSocket → the
  // wave/plans FSM store directly; `activeWave` (above) re-renders from the store.
  // No window listener here — the store reducer owns the transitions (R-A2).

  // Restore active wave on mount
  useEffect(() => {
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch('/api/plans/wave/active');
        if (res.ok) {
          const wave = await res.json();
          // Server-authoritative: a wave with an id sets it active; an empty
          // response CLEARS (so a completion that landed while this tab was
          // unmounted is reflected — the local-useState re-init that used to
          // clear is gone). #1657 Phase 2b.
          useWaveStore.getState().reconcileActiveWave(wave?.id ? wave : null);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  // Fetch wave config (lighthouse mode flag, max concurrent agents)
  useEffect(() => {
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch('/api/plans/wave/config');
        if (res.ok) setWaveConfig(await res.json());
      } catch { /* ignore */ }
    })();
  }, []);

  const handleStartWave = useCallback(async () => {
    if (selectedPlanIds.size === 0) return;
    setWaveStarting(true);
    try {
      const { authFetch } = await import('../api/authFetch');
      const body: { planIds: string[]; lighthouseSessionId?: string } = { planIds: [...selectedPlanIds] };
      if (linkToCurrentSession && currentSessionId) {
        body.lighthouseSessionId = currentSessionId;
      }
      const res = await authFetch('/api/plans/wave/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Failed to start wave' }));
        throw new Error(err.message || 'Failed to start wave');
      }
      const wave = await res.json();
      useWaveStore.getState().reconcileActiveWave(wave);
      setIsSelectMode(false);
      setSelectedPlanIds(new Set());
      setLinkToCurrentSession(false);
    } catch (e: any) {
      setError(e.message || 'Wave start failed');
    } finally {
      setWaveStarting(false);
    }
  }, [selectedPlanIds, linkToCurrentSession, currentSessionId]);

  const togglePlanSelection = useCallback((planId: string) => {
    setSelectedPlanIds(prev => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  }, []);

  // Filter plans
  const filteredPlans = useMemo(() => {
    if (statusFilter === 'all') return plans;
    if (statusFilter === 'executing') return plans.filter(p => p.executionStatus === 'executing' || p.executionStatus === 'awaiting-merge');
    return plans.filter(p => p.status === statusFilter);
  }, [plans, statusFilter]);

  // Group by project
  const groups: ProjectGroup[] = useMemo(() => {
    const map = new Map<string, ProjectPlanSummary[]>();
    for (const p of filteredPlans) {
      const key = p.projectKey;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([project, plans]) => ({ project, plans }));
  }, [filteredPlans]);

  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());

  const toggleProject = useCallback((project: string) => {
    setCollapsedProjects(prev => {
      const next = new Set(prev);
      if (next.has(project)) next.delete(project);
      else next.add(project);
      return next;
    });
  }, []);

  const handleExecuteChunk = useCallback(async (planId: string, chunkId: string) => {
    setExecutingChunks(prev => new Set(prev).add(chunkId));
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch(`/api/plans/chunks/${chunkId}/execute`, { method: 'POST' });
      // Refresh plans to get updated status
      await fetchPlans();
    } catch { /* ignore */ } finally {
      setExecutingChunks(prev => {
        const next = new Set(prev);
        next.delete(chunkId);
        return next;
      });
    }
  }, [fetchPlans]);

  const handleUpdateStatus = useCallback(async (chunkId: string, status: string) => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch(`/api/plans/chunks/${chunkId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      await fetchPlans();
    } catch { /* ignore */ }
  }, [fetchPlans]);

  const handleViewPlan = useCallback((plan: ProjectPlanSummary) => {
    if (plan.planFilePath) {
      window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: plan.planFilePath } }));
    }
  }, []);

  const handleReviewPlan = useCallback((plan: ProjectPlanSummary) => {
    window.dispatchEvent(new CustomEvent('valko-review-plan', { detail: { planId: plan.id, planName: plan.name, projectKey: plan.projectKey } }));
  }, []);

  const handleArchivePlan = useCallback(async (planId: string) => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch(`/api/plans/${planId}/archive`, { method: 'PATCH' });
      await settleAfterAct(planId);
    } catch { /* ignore */ }
  }, [settleAfterAct]);

  const handleDeletePlan = useCallback(async (planId: string) => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch(`/api/plans/${planId}`, { method: 'DELETE' });
      await settleAfterAct(planId);
    } catch { /* ignore */ }
  }, [settleAfterAct]);

  // Pull-to-refresh state
  const [pullRefreshing, setPullRefreshing] = useState(false);
  const pullStartY = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handlePullRefresh = useCallback(async () => {
    setPullRefreshing(true);
    await Promise.all([fetchPlans(), fetchQueueStats()]);
    setPullRefreshing(false);
  }, [fetchPlans, fetchQueueStats]);

  // Queue pause/resume handlers
  const handlePauseQueue = useCallback(async () => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch('/api/plans/queue/pause', { method: 'POST' });
      await fetchQueueStats();
    } catch { /* ignore */ }
  }, [fetchQueueStats]);

  const handleResumeQueue = useCallback(async () => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch('/api/plans/queue/resume', { method: 'POST' });
      await fetchQueueStats();
    } catch { /* ignore */ }
  }, [fetchQueueStats]);

  return (
    <div class="plans-tab" ref={scrollContainerRef}>
      {/* The panel's heading: the hosts name the panel in a tab strip, this names the LIST and carries its count. It is where focus lands after a confirmed Delete or Archive (settleAfterAct). */}
      <h2 class="plans-heading" ref={headingRef} tabIndex={-1}>
        Plans {listKnown && <span class="plans-heading-count">{plans.length}</span>}
      </h2>

      {/* Pull-to-refresh indicator */}
      {pullRefreshing && (
        <div class="plans-pull-refresh">
          <span class="plans-pull-spinner" /> Refreshing...
        </div>
      )}

      {/* Active wave panel */}
      {activeWave && (
        <WaveProgressPanel
          wave={activeWave}
          onClose={() => useWaveStore.getState().closeWave()}
          onRefresh={fetchPlans}
        />
      )}

      {/* Filter bar */}
      <div class="plans-filter-bar">
        {(['all', 'active', 'executing', 'stale'] as StatusFilter[]).map(f => (
          <button
            key={f}
            class={`plans-filter-pill${statusFilter === f ? ' active' : ''}`}
            onClick={() => setPlansStatusFilter(f)}
          >
            {f === 'all' ? `All (${plans.length})` : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <button
          class={`plans-refresh-btn${loading || pullRefreshing ? ' plans-refresh-btn--spinning' : ''}`}
          onClick={handlePullRefresh}
          disabled={loading || pullRefreshing}
        >↻</button>
        <button
          class={`plans-select-btn${isSelectMode ? ' active' : ''}`}
          onClick={() => {
            setIsSelectMode(m => !m);
            setSelectedPlanIds(new Set());
          }}
          title="Multi-select plans for wave execution"
        >
          {isSelectMode ? '✕' : '⊞ Select'}
        </button>
      </div>

      {/* Wave launch bar */}
      {isSelectMode && selectedPlanIds.size > 0 && (
        <div class="plans-wave-bar">
          <span class="plans-wave-bar-count">{selectedPlanIds.size} plan{selectedPlanIds.size !== 1 ? 's' : ''} selected</span>
          {waveConfig?.lighthouseModeEnabled && currentSessionId && (
            <label class="plans-wave-lighthouse-label">
              <input
                type="checkbox"
                checked={linkToCurrentSession}
                onChange={(e) => setLinkToCurrentSession((e.target as HTMLInputElement).checked)}
              />
              {' '}Link to current session
            </label>
          )}
          <button
            class="plans-wave-start-btn"
            onClick={handleStartWave}
            disabled={waveStarting}
          >
            {waveStarting ? '⏳ Starting…' : `▶ Start Wave (${selectedPlanIds.size})`}
          </button>
        </div>
      )}

      {/* Queue stats (admin only) */}
      {isAdmin && queueStats && (
        <div class="plans-queue-stats">
          <span class="plans-queue-depth">Queue: {queueStats.queueDepth}</span>
          <span class="plans-queue-cost">${queueStats.todayCostUsd.toFixed(2)} / ${queueStats.dailyCapUsd.toFixed(2)}</span>
          {queueStats.paused ? (
            <button class="plans-queue-btn plans-queue-btn--resume" onClick={handleResumeQueue}>▶ Resume</button>
          ) : (
            <button class="plans-queue-btn plans-queue-btn--pause" onClick={handlePauseQueue}>⏸ Pause</button>
          )}
          {queueStats.paused && <span class="plans-queue-paused">Paused</span>}
        </div>
      )}

      {/* Content */}
      {error && <div class="plans-error">{error}</div>}

      {!loading && !error && plans.length === 0 && (
        <div class="plans-empty">
          <div class="plans-empty-icon">📋</div>
          <div class="plans-empty-title">No plans yet</div>
          {sandboxFsEnabled ? (
            <div class="plans-empty-hint">
              Set a <strong>Plans source</strong> directory in Settings → Workspace to read <code>.md</code> plans from your sandbox.
            </div>
          ) : (
            <div class="plans-empty-hint">Plans are created via <code>/plan</code> or voice memos</div>
          )}
        </div>
      )}

      {groups.map(g => {
        const isCollapsed = collapsedProjects.has(g.project);
        return (
          <div key={g.project} class="plans-project-group">
            <button class="plans-project-header" onClick={() => toggleProject(g.project)}>
              <span class="plans-project-chevron">{isCollapsed ? '▶' : '▼'}</span>
              <span class="plans-project-name">{g.project}</span>
              <span class="plans-project-count">{g.plans.length}</span>
            </button>
            {!isCollapsed && (
              <div class="plans-project-items">
                {g.plans.map(plan => (
                  <div key={plan.id} class={`plans-card-wrap${isSelectMode ? ' plans-card-wrap--selectable' : ''}`}>
                    {isSelectMode && (
                      <label class="plans-card-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPlanIds.has(plan.id)}
                          onChange={() => togglePlanSelection(plan.id)}
                        />
                      </label>
                    )}
                    <div
                      class={`plans-card-inner${isSelectMode && selectedPlanIds.has(plan.id) ? ' plans-card-inner--selected' : ''}`}
                      onClick={isSelectMode ? () => togglePlanSelection(plan.id) : undefined}
                      style={isSelectMode ? { cursor: 'pointer' } : undefined}
                    >
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        onExecuteChunk={handleExecuteChunk}
                        onUpdateStatus={handleUpdateStatus}
                        onViewPlan={handleViewPlan}
                        onReviewPlan={handleReviewPlan}
                        onArchivePlan={handleArchivePlan}
                        onDeletePlan={handleDeletePlan}
                        executingChunks={executingChunks}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
